import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Axios from "../../api-utility/axiosInstance";

// Fetch Monitoring events/records from backend dynamically
export const fetchMonitoringData = createAsyncThunk(
  "monitoring/fetchData",
  async ({ endpoint, params }, { rejectWithValue }) => {
    try {
      if (!endpoint) {
        // Return mock data/empty data for future unimplemented endpoints
        return { data: [], total_records: 0 };
      }
      const res = await Axios.get(endpoint, { params });
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return res.data?.data || { data: [], total_records: 0 };
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch data from server"
      );
    }
  }
);

// Trigger device synchronization dynamically
export const triggerMonitoringSync = createAsyncThunk(
  "monitoring/triggerSync",
  async ({ endpoint, userId, moduleKey, payload }, { rejectWithValue }) => {
    try {
      if (!endpoint || !userId) {
        return rejectWithValue("Endpoint or User ID missing");
      }
      const url = endpoint.replace(":userId", userId);
      const res = await Axios.post(url, payload || {});
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return { 
        moduleKey, 
        message: res.data?.message || "Sync triggered successfully!" 
      };
    } catch (err) {
      return rejectWithValue({
        moduleKey,
        error: err?.response?.data?.message || "Failed to synchronize device"
      });
    }
  }
);

const monitoringSlice = createSlice({
  name: "monitoring",
  initialState: {
    items: [],
    totalRecords: 0,
    isLoading: false,
    error: null,
    
    // Sync states mapped by moduleKey
    syncStates: {
      // example: sms: { loading: false, success: null, error: null, lastSyncTime: null }
    }
  },
  reducers: {
    clearMonitoringData: (state) => {
      state.items = [];
      state.totalRecords = 0;
      state.isLoading = false;
      state.error = null;
    },
    resetSyncState: (state, action) => {
      const { moduleKey } = action.payload;
      if (state.syncStates[moduleKey]) {
        state.syncStates[moduleKey].success = null;
        state.syncStates[moduleKey].error = null;
        state.syncStates[moduleKey].loading = false;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetching records
      .addCase(fetchMonitoringData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMonitoringData.fulfilled, (state, action) => {
        state.isLoading = false;
        if (Array.isArray(action.payload)) {
          state.items = action.payload;
          state.totalRecords = action.payload.length;
        } else {
          state.items = action.payload?.data || [];
          state.totalRecords = action.payload?.total_records || 0;
        }
      })
      .addCase(fetchMonitoringData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch data";
        state.items = [];
        state.totalRecords = 0;
      })
      
      // Triggering sync
      .addCase(triggerMonitoringSync.pending, (state, action) => {
        const { moduleKey } = action.meta.arg;
        if (!state.syncStates[moduleKey]) {
          state.syncStates[moduleKey] = {};
        }
        state.syncStates[moduleKey].loading = true;
        state.syncStates[moduleKey].success = null;
        state.syncStates[moduleKey].error = null;
      })
      .addCase(triggerMonitoringSync.fulfilled, (state, action) => {
        const { moduleKey } = action.payload;
        state.syncStates[moduleKey].loading = false;
        state.syncStates[moduleKey].success = action.payload.message || "Sync success!";
        state.syncStates[moduleKey].error = null;
        state.syncStates[moduleKey].lastSyncTime = new Date().toISOString();
      })
      .addCase(triggerMonitoringSync.rejected, (state, action) => {
        const { moduleKey } = action.meta.arg;
        const payload = action.payload || {};
        state.syncStates[moduleKey].loading = false;
        state.syncStates[moduleKey].success = null;
        state.syncStates[moduleKey].error = payload.error || "Failed to sync";
      });
  }
});

export const { clearMonitoringData, resetSyncState } = monitoringSlice.actions;
export default monitoringSlice.reducer;
